import { z } from 'zod';

/**
 * The only module that reads `import.meta.env` (vite/RULES.md § Env validation (fail fast)).
 * Every other module imports `env` from here. Validated once, at import time, so a missing or
 * malformed variable fails the app immediately with a readable error instead of surfacing as
 * `undefined` deep inside a request.
 */
export const envSchema = z.object({
  // Vite built-in — always present at runtime. Gates the React Query Devtools (App.tsx).
  DEV: z.boolean(),
  // Local/e2e override only (apps/ui/AGENTS.md § Verification commands) — points the app's own
  // API calls (axiosConfig.ts) at a non-default apps/api port, e.g. when :8080 is already taken.
  // Empty or unset means "use getBaseURL()'s own hostname-based default" (localhost:8080 locally,
  // the production API otherwise) — never required.
  VITE_API_URL: z.string().optional(),
  // Declared by this app's scaffold (utils/constants.ts) but not read anywhere else, and never
  // set in any environment today.
  VITE_BASE_URL: z.string().optional(),
  // Test-only: '1' points Firebase Auth at the local emulator instead of real Google sign-in
  // (firebaseConfig.ts, apps/ui/AGENTS.md § Verification commands). Unset everywhere else, which
  // defaults to '0'; any value other than '0' or '1' fails startup validation.
  VITE_USE_FIREBASE_AUTH_EMULATOR: z.enum(['0', '1']).default('0'),
  // Mapbox GL public token (README.md § Customer Map — "requires VITE_MAPBOX_ACCESS_TOKEN" for
  // the map itself to render). Optional here, so the rest of the app still starts without it —
  // an empty token only breaks the Map page itself (mapbox-gl throws synchronously inside
  // MapPage's render; see playwright.config.ts's own comment on its placeholder token).
  VITE_MAPBOX_ACCESS_TOKEN: z.string().optional(),
  // REQ-2 (apps/ui/README.md § Agent Log): the Chatwoot instance's own base URL and account id,
  // used only to build the conversation link the Agent log page shows next to a turn
  // (src/utils/chatwoot-link.ts) -- apps/api never returns a URL, since this is UI-side config,
  // not part of the domain model (docs/class.puml's own note). Optional, same reasoning as
  // VITE_MAPBOX_ACCESS_TOKEN above: the rest of the app, Agent log included, works without either
  // set -- a turn then shows no conversation link rather than a broken one.
  VITE_CHATWOOT_BASE_URL: z.string().optional(),
  VITE_CHATWOOT_ACCOUNT_ID: z.string().optional()
});

export const env = envSchema.parse(import.meta.env);
